import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material';

/**
 * "Invite member" modal.
 *
 * Submit is disabled until the caller has provided an email AND at
 * least one role. Owner role isn't selectable here — only assignable
 * via a separate ownership-transfer flow (out of scope).
 *
 * Resets local state every time the dialog opens so a previous draft
 * doesn't leak into a fresh invite.
 */
export default function InviteDialog({ open, onClose, availableRoles, onSubmit, isSubmitting }) {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRoles([]);
    }
  }, [open]);

  const handleToggleRole = (role) => {
    setRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const canSubmit = email.trim().length > 0 && roles.length > 0 && !isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='xs'
    >
      <DialogTitle>Invite a member</DialogTitle>
      <DialogContent dividers>
        <Stack spacing='14px' sx={{ pt: '4px' }}>
          <TextField
            fullWidth size='small' required type='email'
            label='Email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='someone@company.com'
            helperText="If they're not on TechProOne yet, we'll email them an invite link."
          />
          <Box>
            <Typography variant='body2' fontWeight='600' mb='6px'>
              Roles <Box component='span' sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <Stack direction='row' spacing='8px' flexWrap='wrap' useFlexGap>
              {(availableRoles || [])
                .filter((r) => r.role !== 'owner')
                .map((r) => {
                  const isSelected = roles.includes(r.role);
                  return (
                    <Button
                      key={r.role}
                      size='small'
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => handleToggleRole(r.role)}
                    >
                      {r.role}
                    </Button>
                  );
                })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!canSubmit}
          onClick={() => onSubmit(email.trim(), roles)}
        >
          {isSubmitting ? 'Sending…' : 'Send invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
